import React, { useEffect, useMemo, useState } from 'react';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import IFormError from '../../interfaces/IFormError';
import debounce from 'lodash.debounce';
import { Autocomplete, Checkbox, CircularProgress, TextField } from '@mui/material';
import ITypeDocument from '../../interfaces/ITypeDocument';
import { useTypeDocumentService } from '../../hooks/services/useTypeDocumentService';

interface IProps {
  setDocumentTypes: (documentTypes: ITypeDocument[]) => void;
  documentTypes: ITypeDocument[];
}

const SearchDocumentType = (props: IProps) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly ITypeDocument[]>([]);
  const loadingInput = open;

  const request = useTypeDocumentService();

  const toastr = useToastrVanila();

  const [errors, setErrors] = useState<IFormError | null>();

  useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await loadDocumentType(' - ');
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingInput]);

  useEffect(() => {
    setErrors(null);
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const loadDocumentType = debounce(async (filter = ' ') => {
    setLoading(true);
    await request
      .search(filter)
      .then(documentType => {
        setOptions(documentType);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(`Erro ao carregar os dados: ${error.message}`);
        setLoading(false);
      });
  }, 500);

  const availableOptions = useMemo(() => {
    const selectedIds = props.documentTypes.map(documentType => documentType.id);
    return [...props.documentTypes, ...options.filter(option => !selectedIds.includes(option.id))];
  }, [options, props.documentTypes]);

  const getLabel = (option: ITypeDocument) => (option?.id ? `${option.id} - ${option.description}` : '');

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      limitTags={2}
      size="small"
      open={open}
      value={props.documentTypes}
      onChange={(e: any, newValue) => {
        props.setDocumentTypes([...newValue]);
      }}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value?.id;
      }}
      getOptionLabel={getLabel}
      renderOption={(optionProps, option, { selected }) => (
        <li {...optionProps} key={option.id}>
          <Checkbox size="small" checked={selected} />
          {getLabel(option)}
        </li>
      )}
      options={availableOptions}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          label="Pesquisar tipo de documento por nome"
          error={Boolean(errors?.code)}
          helperText={errors?.code}
          size="small"
          onChange={e => {
            loadDocumentType(e.target.value);
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchDocumentType;
